import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useChurch } from '../../contexts/ChurchContext';
import './Navigation.css';

const Navigation: React.FC = () => {
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const { currentChurch } = useChurch();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);

  // The bar is fixed, so pages pad for it. Measure it rather than guess: the
  // guess (60px) was wrong whenever the brand wrapped, and the planning header
  // sat under the bar.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty('--nav-height-actual', `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Role checks
  const isViewer = user?.role_name?.toLowerCase() === 'viewer';
  const isStaff = user?.role_name?.toLowerCase() === 'staff';

  const isActive = (path: string) => {
    return location.pathname === path ? 'active' : '';
  };

  const handleLogout = () => {
    logout();
  };

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Navigation links based on role
  const getNavLinks = () => {
    if (isStaff) return [{ path: '/staff', label: 'Staff' }];
    if (isViewer) {
      // Viewer-facing preparation resources
      return [
        { path: '/prepare/upcoming-services', label: 'Upcoming Services' },
        { path: '/prepare/recordings', label: 'Recordings' },
        { path: '/songs/spanish', label: 'Spanish Songs' },
        { path: '/prepare/rehearsal-readings', label: 'Rehearsal Readings' },
      ];
    }

    // Admin and other roles see full navigation
    return [
      { path: '/planning', label: 'Plan' },
      { path: '/season', label: 'Season' },
      { path: '/browse', label: 'Songs' },
      { path: '/prepare', label: 'Prepare' },
      ...(user?.role_name?.toLowerCase() === 'admin' ? [{ path: '/indesign', label: 'InDesign' }, { path: '/staff', label: 'Staff' }] : []),
      { path: '/admin', label: 'Settings' },
    ];
  };

  const navLinks = getNavLinks();

  return (
    <nav className="main-navigation" ref={navRef}>
      <div className="nav-container">
        {isAuthenticated && (
          <button
            className="hamburger-button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle navigation menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        )}

        <Link to={isStaff ? '/staff' : isViewer ? '/prepare/upcoming-services' : '/'} className="nav-brand">
          Liturgical Music
        </Link>

        {isAuthenticated ? (
          <>
            <div className="nav-desktop">
              <ul className="nav-links">
                {navLinks.map(link => (
                  <li key={link.path}>
                    <Link to={link.path} className={`nav-link ${isActive(link.path)}`}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="nav-user">
              <span className="nav-user-info" title={currentChurch?.name ? `Primary church: ${currentChurch.name}` : undefined}>
                {user?.first_name || user?.username}
                {isViewer && <span className="nav-role">Viewer</span>}
              </span>
              <button onClick={handleLogout} className="nav-logout">
                <LogOut size={14} />
                Log out
              </button>
            </div>
          </>
        ) : (
          <div className="nav-auth">
            <Link to="/login" className="btn btn-outline-light btn-sm me-2">
              Login
            </Link>
            <Link to="/signup" className="btn btn-light btn-sm">
              Sign Up
            </Link>
          </div>
        )}

        {isMenuOpen && isAuthenticated && (
          <div className="nav-dropdown-overlay" ref={dropdownRef}>
            <nav className="nav-dropdown">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-dropdown-link ${isActive(link.path)}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <hr className="nav-dropdown-divider" />
              <button
                onClick={() => { handleLogout(); setIsMenuOpen(false); }}
                className="nav-dropdown-logout"
              >
                <LogOut size={16} className="me-2" />
                Log out
              </button>
            </nav>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
